import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { hasAdminPrivileges } from '../../utils/permissionsClient';
import Icon from './icons';
import ProfileMenu, { initials } from './ProfileMenu';
import CommandPalette from './CommandPalette';
import logo from '../DashboardHome/rush-logo.png';
import { hasActiveFocusSession } from '../RushPages/MyQueue/FocusSession';

const FOCUS_PULSE_CSS = `
@keyframes rs-focus-pulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(255, 107, 61, 0)); }
    50% { transform: scale(1.18); filter: drop-shadow(0 0 6px rgba(255, 107, 61, 0.65)); }
}
.rs-side-item.rs-side-focus .ic,
.rs-side-item.rs-side-focus.active .ic {
    color: #ff6b3d;
    animation: rs-focus-pulse 1.4s ease-in-out infinite;
    transform-origin: center;
}
.rs-side-item.rs-side-focus .ic svg { overflow: visible; }
`;
if (typeof document !== 'undefined' && !document.getElementById('rs-focus-pulse-css')) {
    const s = document.createElement('style');
    s.id = 'rs-focus-pulse-css';
    s.textContent = FOCUS_PULSE_CSS;
    document.head.appendChild(s);
}

const SECTIONS = (counts) => [
    {
        label: 'Workspace',
        items: [
            ['Overview', Icon.home, null, '/dashboard'],
            ['All projects', Icon.folder, counts.projects, '/dashboard/projects'],
            ['My queue', Icon.bolt, counts.queue, '/dashboard/queue'],
            ['Inbox', Icon.inbox, counts.inbox, '/dashboard/inbox'],
            ['AI quality', Icon.gauge, null, '/dashboard/ai-quality'],
            ['Reports', Icon.chart, null, '/dashboard/reports'],
        ],
    },
    {
        label: 'Knowledge',
        items: [
            ['Translation memory', Icon.memory, null, '/dashboard/translation-memory'],
            ['Glossaries', Icon.glossary, null, '/dashboard/glossaries'],
            ['Brand voice', Icon.voice, null, '/dashboard/brand-voice'],
            ['Automations', Icon.flow, null, '/dashboard/automations'],
            ['In-context preview', Icon.eye, null, '/dashboard/preview'],
            ['Integrations', Icon.link, null, '/dashboard/sources'],
        ],
    },
    {
        label: 'Team',
        items: [
            ['People', Icon.people, null, '/dashboard/people'],
            ['Settings', Icon.gear, null, '/dashboard/settings'],
        ],
    },
];

const Sidebar = () => {
    const location = useLocation();
    const [cmdOpen, setCmdOpen] = useState(false);
    const [counts, setCounts] = useState({ projects: null, queue: null, inbox: null });
    const [inFocus, setInFocus] = useState(() => hasActiveFocusSession());

    useEffect(() => {
        const recompute = () => setInFocus(hasActiveFocusSession());
        window.addEventListener('rush:focus-changed', recompute);
        // Catches focus state changes from other tabs
        const onStorage = (e) => { if (!e.key || e.key === 'rush.focusSession') recompute(); };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('rush:focus-changed', recompute);
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    const user = useSelector((s) => s.auth.user) || {};
    const token = useSelector((s) => s.auth.token);
    const isAdmin = hasAdminPrivileges(user);

    useEffect(() => {
        const h = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setCmdOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, []);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/projects?summary=true', { headers }).catch(() => ({ data: {} })),
            axios.get('/api/notifications', { headers }).catch(() => ({ data: [] })),
        ]).then(([projRes, notifRes]) => {
            const { createdProjects = [], sharedProjects = [] } = projRes.data || {};
            const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
            const projects = [...unique.values()];
            const active = projects.filter((p) => !p.completed).length;
            const mine = projects.filter((p) => !p.completed && (p.owner === user._id || p.createdBy === user._id)).length;
            const inbox = (notifRes.data || []).filter((n) => !n.read).length;
            setCounts({
                projects: active ? String(active) : null,
                queue: mine ? String(mine) : null,
                inbox: inbox ? String(inbox) : null,
            });
        });
    }, [token, user._id]);

    const sections = [...SECTIONS(counts)];
    if (isAdmin) {
        sections.push({
            label: 'Workspace admin',
            items: [['Admin console', Icon.shield, null, '/dashboard/admin']],
        });
    }

    const isActive = (href) => {
        if (href === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
        return location.pathname.startsWith(href);
    };

    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'You';
    const workspace = user.organization || user.company || 'Workspace';

    return (
        <aside className="rs-side">
            <div className="rs-side-brand">
                <img src={logo} alt="Rush Studio" />
            </div>

            <div
                className="rs-side-search"
                role="button"
                tabIndex={0}
                onClick={() => setCmdOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setCmdOpen(true); }}
            >
                {Icon.search}
                <span className="text">Quick find…</span>
                <span className="kbd">⌘K</span>
            </div>

            {sections.map((g) => (
                <React.Fragment key={g.label}>
                    <div className="rs-side-section">{g.label}</div>
                    {g.items.map(([label, ic, badge, href]) => {
                        const active = isActive(href);
                        return (
                            <Link
                                key={label}
                                to={href}
                                className={`rs-side-item ${active ? 'active' : ''} ${label === 'My queue' && inFocus ? 'rs-side-focus' : ''}`}
                                style={{ textDecoration: 'none' }}
                                title={label === 'My queue' && inFocus ? 'Focus session in progress' : undefined}
                            >
                                <span className="ic">{ic}</span>
                                <span>{label}</span>
                                {badge && (
                                    <span className={`badge ${label === 'Inbox' ? 'dot' : ''}`}>{badge}</span>
                                )}
                            </Link>
                        );
                    })}
                </React.Fragment>
            ))}

            <ProfileMenu
                placement="up"
                trigger={(open) => (
                    <div className={`rs-side-foot ${open ? 'open' : ''}`} role="button" tabIndex={0}>
                        <div className="av-me">{initials(user)}</div>
                        <div className="who">
                            <b>{name}</b>
                            <span>{workspace}</span>
                        </div>
                        <span className="opts">{Icon.more}</span>
                    </div>
                )}
            />

            {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} />}
        </aside>
    );
};

export default Sidebar;
